var _InsertDriveFileIconF, _Delete, _MoreVert;
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState, useEffect, useRef } from 'react';
import { any, arrayOf, bool, element, func, number, object, oneOfType, shape, string } from 'prop-types';
import { buttonClasses } from '@mui/material/Button';
import clsx from 'clsx';
import { styled } from '@mui/material/styles';
import { Delete, InsertDriveFile, MoreVert } from 'mosaic-react-icons';
import SovosTypography from '../sovos-typography';
import SovosButton from '../sovos-button';
import SovosIconButton from '../sovos-icon-button';
import SovosIconMenu from '../sovos-icon-menu';
import SovosTooltip from '../sovos-tooltip';
import SovosConfirmationDialog from '../sovos-confirmation-dialog';
import calculateBytes from '../internals/utils/calculateBytes';
import { useResize } from '../hooks';
import useMosaicTranslation from '../internals/i18n/useMosaicTranslation';
const getActionIconStyles = _ref => {
  let {
    palette,
    spacing
  } = _ref;
  return {
    color: palette.text.primary,
    marginInlineEnd: "-".concat(spacing(0.5)),
    opacity: 0,
    '&:focus-visible': {
      opacity: 1
    }
  };
};
const Root = styled('div')(_ref2 => {
  let {
    theme: {
      palette,
      spacing
    }
  } = _ref2;
  return {
    alignItems: 'center',
    backgroundColor: palette.common.white,
    display: 'flex',
    minHeight: spacing(5),
    justifyContent: 'space-between',
    margin: 0,
    padding: 0,
    paddingInlineEnd: spacing(),
    '&.sovosFile--hovered': {
      '& .sovosFile__deleteButton, & .sovosFile__actionsMenu': {
        opacity: 1
      }
    }
  };
});
const FileDownloadButton = styled(SovosButton)(_ref3 => {
  let {
    theme: {
      palette,
      spacing
    }
  } = _ref3;
  return {
    border: '2px solid transparent',
    borderRadius: 0,
    display: 'flex',
    justifyContent: 'flex-start',
    flex: '1 1 auto',
    height: spacing(5),
    overflow: 'hidden',
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
      cursor: 'pointer',
      '& .sovosFile__fileName, .sovosFile__fileIcon': {
        color: palette.primary.main
      }
    },
    ["&.".concat(buttonClasses.focusVisible)]: {
      border: "2px solid ".concat(palette.primary.main)
    }
  };
});
const InsertDriveFileIconFileIcon = styled(InsertDriveFile)(_ref4 => {
  let {
    theme: {
      palette,
      spacing
    }
  } = _ref4;
  return {
    color: palette.grey[400],
    marginInlineStart: spacing()
  };
});
const FileText = styled('div')(_ref5 => {
  let {
    theme: {
      spacing
    }
  } = _ref5;
  return {
    alignItems: 'center',
    border: '2px solid transparent',
    display: 'flex',
    flex: '1 1 auto',
    overflow: 'hidden',
    marginInlineEnd: spacing()
  };
});
const FileName = styled(SovosTypography)(_ref6 => {
  let {
    theme: {
      spacing
    }
  } = _ref6;
  return {
    flex: '0 1 auto',
    marginBlockEnd: 0,
    marginBlockStart: 0,
    marginInlineEnd: 0,
    marginInlineStart: spacing(),
    maxWidth: '80%',
    overflow: 'hidden',
    textAlign: 'left',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  };
});
const FileSize = styled(SovosTypography)(_ref7 => {
  let {
    theme: {
      spacing
    }
  } = _ref7;
  return {
    flex: '0 0 auto',
    marginInlineStart: spacing(0.5)
  };
});
const MetaData = styled('div')(_ref8 => {
  let {
    theme: {
      spacing
    }
  } = _ref8;
  return {
    alignItems: 'center',
    display: 'flex',
    flex: '0 1 auto',
    justifyContent: 'space-between',
    marginInlineStart: spacing()
  };
});
const UserName = styled(SovosTypography)(_ref9 => {
  let {
    theme: {
      spacing
    }
  } = _ref9;
  return {
    lineHeight: 1,
    marginInlineEnd: spacing(2),
    textAlign: 'right'
  };
});
const TimeStamp = styled(SovosTypography)(() => ({
  flex: '1 1 auto',
  lineHeight: 1,
  textAlign: 'left'
}));
const DeleteBtn = styled(SovosIconButton)(_ref10 => {
  let {
    theme
  } = _ref10;
  return getActionIconStyles(theme);
});
const FileActions = styled(SovosIconMenu)(_ref11 => {
  let {
    theme
  } = _ref11;
  return getActionIconStyles(theme);
});
const SovosFile = _ref12 => {
  let {
    actionMenuItems,
    classes,
    className,
    confirmDelete = false,
    disabled = false,
    FileDeleteConfirmationProps,
    fileInfo,
    onClick,
    onDelete,
    ...rest
  } = _ref12;
  const {
    t
  } = useMosaicTranslation();
  const [fileNameDoesOverflow, setFileNameDoesOverflow] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [buttonIsFocused, setButtonIsFocused] = useState(false);
  const [isWide, setIsWide] = useState(undefined);
  const [hovered, setHovered] = useState(false);
  const fileRoot = useRef(null);
  const btnFileName = useRef(null);
  const [{
    width: fileWidth
  }] = useResize(fileRoot);
  useEffect(() => {
    const nameWidth = btnFileName.current.offsetWidth;
    const nameScrollWidth = btnFileName.current.scrollWidth;
    setFileNameDoesOverflow(nameScrollWidth > nameWidth);
    if (fileWidth > 480) {
      setIsWide(true);
    } else {
      setIsWide(false);
    }
  }, [fileWidth]);
  const openConfirmDelete = () => setDeleteConfirmOpen(true);
  const handleDelete = () => {
    setDeleteConfirmOpen(false);
    onDelete(fileInfo);
  };
  const handleDeleteKeyUp = event => {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      handleDelete();
    }
    return null;
  };
  const closeDeleteDialog = () => setDeleteConfirmOpen(false);
  const {
    documentLink,
    name,
    bytes,
    uploadedBy,
    uploadedDate
  } = fileInfo;
  const uploadedByIsString = typeof uploadedBy === 'string';
  const hasUserName = () => {
    if (uploadedByIsString) {
      if (uploadedBy.length) return true;
      return false;
    }
    return uploadedBy && !!(uploadedBy.name || uploadedBy.surname);
  };
  const getFileToolTipText = () => {
    let tooltipText = '';
    if (fileNameDoesOverflow && name) tooltipText += "".concat(name, " ");
    if (uploadedDate && !isWide) tooltipText += "".concat(uploadedDate, " ");
    if (uploadedBy && !isWide) {
      tooltipText += uploadedByIsString ? "by ".concat(uploadedBy) : "by ".concat(uploadedBy.name, " ").concat(uploadedBy.surname);
    }
    return tooltipText;
  };
  const renderFileText = () => /*#__PURE__*/React.createElement(React.Fragment, null, _InsertDriveFileIconF || (_InsertDriveFileIconF = /*#__PURE__*/React.createElement(InsertDriveFileIconFileIcon, {
    className: "sovosFile__fileIcon",
    fontSize: "small"
  })), /*#__PURE__*/React.createElement(FileName, {
    className: "sovosFile__fileName",
    color: "text.primary",
    ref: btnFileName,
    variant: "body1"
  }, name), bytes && /*#__PURE__*/React.createElement(FileSize, {
    color: "text.secondary",
    variant: "body1"
  }, "(", calculateBytes(bytes), ")"));
  return /*#__PURE__*/React.createElement(Root, _extends({
    className: clsx('sovosFile', className, classes === null || classes === void 0 ? void 0 : classes.root, {
      'sovosFile--hovered': hovered
    }),
    onPointerEnter: () => setHovered(true),
    onPointerLeave: () => setHovered(false),
    ref: fileRoot
  }, rest), /*#__PURE__*/React.createElement(SovosTooltip, {
    title: getFileToolTipText()
  }, onClick || documentLink ? /*#__PURE__*/React.createElement(FileDownloadButton, {
    className: clsx('sovosFile__fileButton', classes === null || classes === void 0 ? void 0 : classes.fileDownloadButton),
    color: "inherit",
    "data-testid": "fileButton",
    disabled: disabled,
    disableRipple: true,
    onBlur: () => setButtonIsFocused(false),
    onClick: documentLink ? () => window.open(documentLink, '_blank', 'noreferrer') : onClick,
    onFocus: () => setButtonIsFocused(true),
    onKeyUp: buttonIsFocused && onDelete ? handleDeleteKeyUp : null,
    variant: "text"
  }, renderFileText()) : /*#__PURE__*/React.createElement(FileText, null, renderFileText())), isWide && /*#__PURE__*/React.createElement(MetaData, null, hasUserName() && /*#__PURE__*/React.createElement(UserName, {
    variant: "body1",
    color: "text.secondary"
  }, uploadedByIsString ? uploadedBy : uploadedBy.name, ' ', uploadedByIsString ? null : uploadedBy.surname), uploadedDate && /*#__PURE__*/React.createElement(TimeStamp, {
    component: "time",
    variant: "body1",
    color: "text.secondary"
  }, uploadedDate)), onDelete && /*#__PURE__*/React.createElement(DeleteBtn, {
    className: clsx('sovosFile__deleteButton', classes === null || classes === void 0 ? void 0 : classes.deleteBtn),
    disabled: disabled,
    onClick: confirmDelete ? openConfirmDelete : handleDelete,
    size: "small",
    tooltipText: (FileDeleteConfirmationProps === null || FileDeleteConfirmationProps === void 0 ? void 0 : FileDeleteConfirmationProps.defaultActionButtonLabel) || t('delete')
  }, _Delete || (_Delete = /*#__PURE__*/React.createElement(Delete, null))), actionMenuItems && /*#__PURE__*/React.createElement(FileActions, {
    className: "sovosFile__actionsMenu",
    disabled: disabled,
    iconElement: _MoreVert || (_MoreVert = /*#__PURE__*/React.createElement(MoreVert, null)),
    onClose: () => setHovered(false),
    size: "small"
  }, actionMenuItems), confirmDelete && /*#__PURE__*/React.createElement(SovosConfirmationDialog, {
    title: (FileDeleteConfirmationProps === null || FileDeleteConfirmationProps === void 0 ? void 0 : FileDeleteConfirmationProps.defaultTitleText) || t('file.confirmDelete'),
    content: (FileDeleteConfirmationProps === null || FileDeleteConfirmationProps === void 0 ? void 0 : FileDeleteConfirmationProps.defaultContentText) || t('file.confirmDeleteContent'),
    open: deleteConfirmOpen,
    ActionButtonProps: {
      children: (FileDeleteConfirmationProps === null || FileDeleteConfirmationProps === void 0 ? void 0 : FileDeleteConfirmationProps.defaultActionButtonLabel) || t('delete'),
      onClick: handleDelete
    },
    CancelButtonProps: {
      children: (FileDeleteConfirmationProps === null || FileDeleteConfirmationProps === void 0 ? void 0 : FileDeleteConfirmationProps.defaultCancelButtonLabel) || t('cancel'),
      onClick: closeDeleteDialog
    },
    negative: true,
    onClose: closeDeleteDialog
  }));
};
SovosFile.propTypes = {
  /**
   * A list of actions to render in a drop down menu. Expected to be
   * `SovosMenuItem` elements.
   */
  actionMenuItems: arrayOf(element),
  /**
   * Override or extend the styles applied to the component
   */
  classes: object,
  /**
   * Extend the class name applied to the root element
   */
  className: string,
  /**
   * When true, a confirmation dialog will be displayed before onDelete is fired
   */
  confirmDelete: bool,
  /**
   * If true, buttons are disabled
   */
  disabled: bool,
  /**
   * Object of strings for the delete confirmation dialog
   */
  FileDeleteConfirmationProps: shape({
    defaultTitleText: string,
    defaultContentText: string,
    defaultActionButtonLabel: string,
    defaultCancelButtonLabel: string
  }),
  /**
   * An object of information describing the file
   */
  fileInfo: shape({
    bytes: number,
    downloadLink: string,
    id: any,
    name: string,
    uploadedDate: string,
    uploadedBy: oneOfType([shape({
      name: string,
      surname: string
    }), string])
  }).isRequired,
  /**
   * Callback fired when the file is clicked
   */
  onClick: func,
  /**
   * Callback fired when the delete icon is clicked. When not present, a
   * delete button will not be displayed `function(fileInfo: object) => void`
   */
  onDelete: func
};
export default SovosFile;